import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

// 🧩 Header component: displays logo, portal name, navigation links, and admin profile
const Header = ({ token, onLogout, adminData }) => {
  // 🔄 State to control hamburger menu visibility (for mobile)
  const [menuOpen, setMenuOpen] = useState(false);

  // 🔐 State to track admin login status
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 👤 State to toggle admin profile dropdown
  const [showDropdown, setShowDropdown] = useState(false);

  // 📍 Get current route for active link highlighting
  const location = useLocation();

  // 📌 Toggle hamburger menu open/close
  const toggleMenu = () => setMenuOpen(prev => !prev);

  // 📌 Toggle admin dropdown open/close
  const toggleDropdown = () => setShowDropdown(prev => !prev);

  // ✅ Highlight active navigation link
  const isActive = (path) => location.pathname === path ? 'active-link' : '';

  // 🆔 Extract initials from admin name for fallback avatar
  const getInitials = (name) =>
    name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'AD';

  // 🧹 Close menu and dropdown when a link is clicked
  const handleLinkClick = () => {
    setMenuOpen(false);
    setShowDropdown(false);
  };

  // 🔍 Check for stored token on route change to update login status
  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    setIsLoggedIn(!!storedToken);
  }, [location]);

  return (
    <header className="header-container">
      {/* 🔰 Logo section */}
      <div className="logo-area">
        <img src="/images/logo.png" alt="GovJob Logo" className="logo-image" />
      </div>

      {/* 🏷️ Portal title */}
      <div className="title-area">
        <span className="logo-text">Our GovJob Portal</span>
      </div>

      {/* 🧭 Navigation area */}
      <div className="nav-area">
        {/* ☰ Hamburger icon for mobile */}
        <button className={`menu-toggle ${menuOpen ? 'open' : ''}`} onClick={toggleMenu}>
          <span className="bar top-bar"></span>
          <span className="bar middle-bar"></span>
          <span className="bar bottom-bar"></span>
        </button>

          {/* 🔗 Navigation links */}
        <nav className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <Link to="/" className={isActive('/')} onClick={handleLinkClick}>Home</Link>

          {/* 📂 Jobs dropdown */}
          <div className="dropdown">
            <button className="dropdown-toggle">Jobs ▾</button>
            <div className="dropdown-menu">
              <Link to="/jobs/government" className={isActive('/jobs/government')} onClick={handleLinkClick}>Government</Link>
              <Link to="/jobs/defence" className={isActive('/jobs/defence')} onClick={handleLinkClick}>Defence</Link>
              <Link to="/jobs/psu" className={isActive('/jobs/psu')} onClick={handleLinkClick}>PSU</Link>
              <Link to="/jobs/railways" className={isActive('/jobs/railways')} onClick={handleLinkClick}>Railways</Link>              
              <Link to="/jobs/banking" className={isActive('/jobs/banking')} onClick={handleLinkClick}>Banking</Link>
              <Link to="/jobs/teaching" className={isActive('/jobs/teaching')} onClick={handleLinkClick}>Teaching</Link>
            </div>
          </div>
          <Link to="/tools" className={isActive('/tools')} onClick={handleLinkClick}>Tools</Link>

          {/* 🔐 Admin login or dashboard */}
          {!isLoggedIn ? (
            <Link to="/admin/login" className={isActive('/admin/login')} onClick={handleLinkClick}>Admin</Link>
          ) : (
            <>
              <Link to="/admin/dashboard" className={isActive('/admin/dashboard')} onClick={handleLinkClick}>🛡️ Admin Panel</Link>

              {/* 👤 Admin profile avatar and dropdown */}
              <div className="profile-container" onClick={toggleDropdown}>
                {adminData?.avatarUrl ? (
                  <img src={adminData.avatarUrl} alt="Admin" className="avatar-image" />
                ) : (
                  <div className="avatar-fallback">{getInitials(adminData?.name)}</div>
                )}

                {/* 📂 Dropdown menu for admin actions */}
                {showDropdown && (
                  <div className="profile-dropdown">
                    <Link to="/admin/account" onClick={handleLinkClick}>👤 Account</Link>
                    <Link to="/admin/change-password" onClick={handleLinkClick}>🔒 Change Password</Link>
                    <button onClick={() => {
                      onLogout();
                      window.location.href = '/';
                    }}>🚪 Logout</button>
                  </div>
                )}
              </div>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
